import type {
  DocumentTab,
  TabHistoryItem,
} from 'features/knowledgeDocumentViewer/types'
import {
  createBrowseAllDocumentsTab,
  createDestinationTab,
  createWorkspaceHomeTab,
} from 'features/knowledgeDocumentViewer/utils/tabStateUrlHelpers'
import { WORKSPACE_SETTINGS_DESTINATION } from 'features/navbar/configSettings/workspaceSettingsLink'
import { SelectedTab } from 'features/navbar/types'

import { findReuseTargetTab } from './findReuseTargetTab'
import {
  createResourceTab,
  createWorkspaceDocumentTab,
  destinationResourceKey,
  documentResourceKey,
  type OpenDocumentTabOptions,
  STABLE_IDENTITY_DESTINATIONS,
} from './resourceTabs'

export interface DestinationTabOptions {
  destinationKey: string
  label: string
  href: string
  workspaceDestination?: SelectedTab
  resourceKey?: string
}

/** What a tab should show next: a destination page or a document. */
export type NavigateActiveTabOptions =
  | ({ kind: 'destination' } & DestinationTabOptions)
  | ({ kind: 'document' } & OpenDocumentTabOptions)

/**
 * The one way a destination becomes a tab, shared by sidebar clicks and the
 * palette.
 */
export function createTabForDestination(
  options: DestinationTabOptions
): DocumentTab {
  if (
    options.destinationKey === SelectedTab.Home ||
    options.destinationKey === SelectedTab.DocumentsHome
  ) {
    return createWorkspaceHomeTab()
  }
  if (options.destinationKey === SelectedTab.Documents) {
    return createBrowseAllDocumentsTab()
  }
  if (options.destinationKey === WORKSPACE_SETTINGS_DESTINATION.evaluations) {
    return {
      ...createDestinationTab(options),
      resourceKey: destinationResourceKey(options.destinationKey),
    }
  }
  if (STABLE_IDENTITY_DESTINATIONS.has(options.destinationKey)) {
    return createResourceTab({
      resourceKey: destinationResourceKey(options.destinationKey),
      workspaceDestination: options.destinationKey as SelectedTab,
      label: options.label,
      href: options.href,
    })
  }
  return {
    ...createDestinationTab(options),
    workspaceDestination: options.workspaceDestination,
    resourceKey: options.resourceKey,
  }
}

export function createFreshTabFor(
  options: NavigateActiveTabOptions,
  resourceKey: string | undefined
): DocumentTab {
  if (options.kind === 'document') {
    return createWorkspaceDocumentTab(
      options,
      resourceKey ?? `document:${crypto.randomUUID()}`
    )
  }
  return createTabForDestination({ ...options, resourceKey })
}

export function resourceKeyForNavigation(
  options: NavigateActiveTabOptions,
  scopeMode: 'active' | 'preview'
): string {
  return options.kind === 'document'
    ? documentResourceKey(scopeMode, options.documentId)
    : (options.resourceKey ?? destinationResourceKey(options.destinationKey))
}

const showsDocument = (tab: DocumentTab, documentId: string): boolean => {
  const item = tab.history[tab.currentIndex]
  return item.type === 'document' && item.documentId === documentId
}

export function findTabShowingTarget(
  tabs: readonly DocumentTab[],
  options: NavigateActiveTabOptions,
  resourceKey: string
): DocumentTab | undefined {
  if (options.kind === 'document') {
    return tabs.find(
      (tab) =>
        tab.resourceKey === resourceKey ||
        showsDocument(tab, options.documentId)
    )
  }
  return options.resourceKey
    ? tabs.find((tab) => tab.resourceKey === resourceKey)
    : findReuseTargetTab(tabs, options)
}

/** Everything the documents page renders: search landings, chats, documents. */
export const isDocumentsViewerItem = (item: TabHistoryItem): boolean =>
  item.type === 'document' || item.type === 'search'

export function pushDocumentOntoTab(
  tab: DocumentTab,
  options: OpenDocumentTabOptions,
  resourceKey: string
): DocumentTab {
  return {
    ...tab,
    history: [
      ...tab.history.slice(0, tab.currentIndex + 1),
      {
        type: 'document',
        documentId: options.documentId,
        pageNumber: options.pageNumber ?? 1,
        selectedBoxIds: [],
        query: '',
        fragmentHighlight: options.fragmentHighlight,
        regionHighlight: options.regionHighlight,
      },
    ],
    currentIndex: tab.currentIndex + 1,
    customLabel: options.label ?? tab.customLabel,
    resourceKey,
    dirty: undefined,
  }
}
